import type { BlockyTimeDB } from '@/db/schema'
import { db as defaultDb } from '@/db/schema'
import type { EntryRow } from '@/db/types'
import { logger } from '@/core/logging/logger'

/**
 * Repairs slots holding more than one live entry.
 *
 * Sync can genuinely produce this: both devices mark 09:00 while offline, each
 * generating its own UUID. Neither push conflicts -- the ids differ -- so the
 * server accepts both and each device pulls the other's. Last-write-wins does
 * nothing, because these are not competing versions of one row; they are two
 * rows that happen to occupy the same minute.
 *
 * The survivor is chosen deterministically, so both devices reach the same
 * answer without coordinating: newest `updatedAt` wins, ties broken by the
 * higher `deviceId`. This is the same rule the server uses for conflicts.
 *
 * Must run after every pull that touched entries -- 07-sync-protocol.md §7.
 */
export async function repairSlotDuplicates(
  dayKeys: readonly number[],
  db: BlockyTimeDB = defaultDb,
): Promise<number> {
  if (dayKeys.length === 0) return 0

  const now = Date.now()
  let repaired = 0

  await db.transaction('rw', db.entries, async () => {
    for (const dayKey of new Set(dayKeys)) {
      const live = (await db.entries.where('dayKey').equals(dayKey).toArray()).filter(
        (e) => e.deletedAt === null,
      )

      const bySlot = new Map<number, EntryRow[]>()
      for (const entry of live) {
        const list = bySlot.get(entry.slotIndex)
        if (list) list.push(entry)
        else bySlot.set(entry.slotIndex, [entry])
      }

      for (const rows of bySlot.values()) {
        if (rows.length < 2) continue

        // Descending: rows[0] survives.
        rows.sort((a, b) => b.updatedAt - a.updatedAt || b.deviceId.localeCompare(a.deviceId))

        for (const loser of rows.slice(1)) {
          await db.entries.put({
            ...loser,
            deletedAt: now,
            updatedAt: now,
            // Dirty, so the tombstone travels to the other device. Both will
            // have chosen the same survivor, so this converges rather than
            // ping-ponging.
            _dirty: 1,
          })
          repaired += 1
        }
      }
    }
  })

  if (repaired > 0) {
    logger.info('sync', `Repaired ${repaired} duplicate slot(s)`)
  }
  return repaired
}
